import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DATE_FORMAT_YYYYMMDD } from "utils/dateutils";
import { Form, message } from "antd";
import MaterialDateConfirmationFooter, {
  SUBMIT_DISABLED_DATE_ORDER_ERROR_TOOLTIP
} from "./material-date-confirmation-footer";
import MaterialDateConfirmationHeader from "./material-date-confirmation-header";
import MaterialDetailsSidebar from "./material-details-sidebar";
import MaterialTimelineSection from "./material-timeline-section";
import MaterialContentCommentsLayout from "./material-content-comments-layout";
import MaterialComments from "./material-comments";
import {
  mapDateConfirmationDataToApiPayload,
  mapMaterialToDateConfirmationData,
  mapOffsetsToApiPayload
} from "./map-api-to-view-model";
import { todayInProjectTimezone } from "./project-calendar-date";
import {
  getActualDateErrorMilestoneIds,
  hasActualDateOrderErrors
} from "./timeline/timeline-actual-date-validation";
import {
  getProjectedDateErrorMilestoneIds,
  hasProjectedDateOrderErrors
} from "./timeline/timeline-projected-date-validation";
import { updateLeadTimeRequestDetails } from "../services";
import "./material-date-confirmation.scss";
import {
  MaterialDateConfirmationData,
  MaterialDateConfirmationMainContentProps,
  MaterialDateConfirmationMilestone,
  ProjectCalendarOffs
} from "./types";
import { EstimateStatus, IMaterial } from "../type-definitions";
import MaterialDurationsSection from "../material-durations-section";
import { getMaterialTimelineVariant } from "./timeline-variant";

type InitialMilestoneDates = {
  projectedDate: string | null;
  actualDate: string | null;
};

function hasAllMilestoneActualDatesFromApi(material: IMaterial): boolean {
  const milestones = material.milestones ?? [];
  return (
    milestones.length > 0 &&
    milestones.every((milestone) => Boolean(milestone.actual_date))
  );
}

function applyMilestoneUpdatesWithActualDateCascade(
  milestones: MaterialDateConfirmationMilestone[],
  milestoneId: string,
  updates: Partial<
    Pick<MaterialDateConfirmationMilestone, "projectedDate" | "actualDate">
  >,
  initialMilestoneDatesById?: Map<string, InitialMilestoneDates>
): MaterialDateConfirmationMilestone[] {
  const milestoneIndex = milestones.findIndex(
    (milestone) => milestone.id === milestoneId
  );
  const shouldClearSubsequentActualDates =
    updates.actualDate === null && milestoneIndex >= 0;

  let resolvedUpdates = updates;
  if ("actualDate" in updates && updates.actualDate != null) {
    const initial = initialMilestoneDatesById?.get(milestoneId);
    if (initial) {
      resolvedUpdates = {
        ...updates,
        projectedDate: initial.projectedDate ?? null
      };
    }
  }

  return milestones.map((milestone, index) => {
    if (milestone.id === milestoneId) {
      return { ...milestone, ...resolvedUpdates };
    }

    if (
      shouldClearSubsequentActualDates &&
      index > milestoneIndex &&
      milestone.actualDate
    ) {
      return { ...milestone, actualDate: null };
    }

    return milestone;
  });
}

function MaterialDateConfirmationMainContent({
  data,
  timezoneId,
  calendarOffs,
  submitting = false,
  hasDateChanges = false,
  hasProjectedDateOrderError = false,
  hasActualDateOrderError = false,
  projectedDateErrorIds,
  actualDateErrorIds,
  isFullyConfirmedOnLoad = false,
  projectedDateLabel,
  expectedOnsiteDateLabel,
  forceFirstMilestoneReadOnly,
  canMarkCompleteWhenNoPrevious,
  disableFirstMilestoneSetActual,
  onMilestoneChange,
  onCommentsChange,
  onMarkMilestoneComplete,
  onConfirmDates
}: MaterialDateConfirmationMainContentProps) {
  return (
    <div
      className="material-date-confirmation flex flex-col lg:flex-row h-full bg-white overflow-hidden"
      data-testid="material-date-confirmation-main-content"
    >
      <div className="material-date-confirmation-main flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden lg:border-0 lg:border-r lg:border-solid lg:border-r-ciq-gray-400">
        <MaterialDateConfirmationHeader
          data={data}
          detailsData={data}
          expectedOnsiteDateLabel={expectedOnsiteDateLabel}
        />
        <MaterialTimelineSection
          milestones={data.milestones}
          timezoneId={timezoneId}
          calendarOffs={calendarOffs}
          comments={data.comments}
          commentsDisabled={isFullyConfirmedOnLoad}
          projectedDateLabel={projectedDateLabel}
          forceFirstMilestoneReadOnly={forceFirstMilestoneReadOnly}
          canMarkCompleteWhenNoPrevious={canMarkCompleteWhenNoPrevious}
          disableFirstMilestoneSetActual={disableFirstMilestoneSetActual}
          projectedDateErrorIds={projectedDateErrorIds}
          actualDateErrorIds={actualDateErrorIds}
          onCommentsChange={onCommentsChange}
          onMilestoneChange={onMilestoneChange}
          onMarkMilestoneComplete={onMarkMilestoneComplete}
        />
        <MaterialDateConfirmationFooter
          submitting={submitting}
          hasDateChanges={hasDateChanges}
          disabled={
            isFullyConfirmedOnLoad ||
            hasProjectedDateOrderError ||
            hasActualDateOrderError
          }
          disabledTooltip={
            hasProjectedDateOrderError || hasActualDateOrderError
              ? SUBMIT_DISABLED_DATE_ORDER_ERROR_TOOLTIP
              : undefined
          }
          onConfirmDates={onConfirmDates}
        />
      </div>
      <div className="hidden lg:block shrink-0 lg:h-full">
        <MaterialDetailsSidebar data={data} />
      </div>
    </div>
  );
}

export function useMaterialDateConfirmationState(
  initialData: MaterialDateConfirmationData,
  timezoneId: string,
  token?: string,
  material?: IMaterial,
  onSubmitSuccess?: () => void
) {
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const materialVersionKey = material
    ? `${material.id}:${material.updated_at ?? ""}:${material.estimate_status}`
    : undefined;

  useEffect(() => {
    setData(initialDataRef.current);
  }, [materialVersionKey]);

  const initialMilestoneDatesById = useMemo(
    () =>
      new Map(
        initialData.milestones.map((milestone) => [
          milestone.id,
          {
            projectedDate: milestone.projectedDate ?? null,
            actualDate: milestone.actualDate ?? null
          }
        ])
      ),
    [initialData.milestones]
  );

  const onMilestoneChange = useCallback<
    MaterialDateConfirmationMainContentProps["onMilestoneChange"]
  >(
    (milestoneId, updates) => {
      setData((prev) => ({
        ...prev,
        milestones: applyMilestoneUpdatesWithActualDateCascade(
          prev.milestones,
          milestoneId,
          updates,
          initialMilestoneDatesById
        )
      }));
    },
    [initialMilestoneDatesById]
  );

  const onCommentsChange = useCallback<
    MaterialDateConfirmationMainContentProps["onCommentsChange"]
  >((comments) => {
    setData((prev) => ({ ...prev, comments }));
  }, []);

  const onMarkMilestoneComplete = useCallback(
    (milestoneId: string, isComplete: boolean) => {
      setData((prev) => ({
        ...prev,
        milestones: applyMilestoneUpdatesWithActualDateCascade(
          prev.milestones,
          milestoneId,
          {
            actualDate: isComplete
              ? todayInProjectTimezone(timezoneId).format(DATE_FORMAT_YYYYMMDD)
              : null
          },
          initialMilestoneDatesById
        )
      }));
    },
    [timezoneId, initialMilestoneDatesById]
  );

  const projectedDateValidationOptions = useMemo(
    () => ({
      allowEmptyProjectedDate: getMaterialTimelineVariant(
        material?.dateblock_compute_strategy
      ).allowEmptyProjectedDate
    }),
    [material?.dateblock_compute_strategy]
  );

  const projectedDateErrorIds = useMemo(
    () =>
      getProjectedDateErrorMilestoneIds(
        data.milestones,
        projectedDateValidationOptions
      ),
    [data.milestones, projectedDateValidationOptions]
  );

  const hasProjectedDateOrderError = useMemo(
    () =>
      hasProjectedDateOrderErrors(
        data.milestones,
        projectedDateValidationOptions
      ),
    [data.milestones, projectedDateValidationOptions]
  );

  const hasActualDateOrderError = useMemo(
    () => hasActualDateOrderErrors(data.milestones),
    [data.milestones]
  );

  const actualDateErrorIds = useMemo(
    () => getActualDateErrorMilestoneIds(data.milestones),
    [data.milestones]
  );

  const onConfirmDates = useCallback(async () => {
    if (hasProjectedDateOrderError || hasActualDateOrderError) {
      return;
    }

    setSubmitting(true);
    try {
      if (!token || !material) {
        message.error("Failed to confirm dates");
        return;
      }

      const payload = mapDateConfirmationDataToApiPayload(material, data);

      const response = await updateLeadTimeRequestDetails(token, payload);
      if (response.success) {
        onSubmitSuccess?.();
      } else {
        message.error("Failed to confirm dates");
      }
    } catch {
      message.error("Failed to confirm dates");
    } finally {
      setSubmitting(false);
    }
  }, [
    data,
    hasActualDateOrderError,
    hasProjectedDateOrderError,
    token,
    material,
    onSubmitSuccess
  ]);

  const hasDateChanges = useMemo(
    () =>
      data.milestones.some((milestone) => {
        const initialMilestone = initialMilestoneDatesById.get(milestone.id);
        if (!initialMilestone) return false;
        return (
          (milestone.projectedDate ?? null) !==
            initialMilestone.projectedDate ||
          (milestone.actualDate ?? null) !== initialMilestone.actualDate
        );
      }),
    [data.milestones, initialMilestoneDatesById]
  );

  return {
    data,
    submitting,
    onMilestoneChange,
    onCommentsChange,
    onMarkMilestoneComplete,
    onConfirmDates,
    hasDateChanges,
    hasProjectedDateOrderError,
    projectedDateErrorIds,
    hasActualDateOrderError,
    actualDateErrorIds
  };
}

export default MaterialDateConfirmationMainContent;

export function MaterialDateConfirmationView({
  material,
  timezoneId,
  token,
  calendarOffs,
  onSubmitSuccess
}: {
  material: IMaterial;
  timezoneId: string;
  token?: string;
  calendarOffs?: ProjectCalendarOffs;
  onSubmitSuccess?: () => void;
}) {
  const initialData = useMemo(
    () => mapMaterialToDateConfirmationData(material, timezoneId),
    [material, timezoneId]
  );
  const {
    data,
    submitting,
    onMilestoneChange,
    onCommentsChange,
    onMarkMilestoneComplete,
    onConfirmDates,
    hasDateChanges,
    hasProjectedDateOrderError,
    projectedDateErrorIds,
    hasActualDateOrderError,
    actualDateErrorIds
  } = useMaterialDateConfirmationState(
    initialData,
    timezoneId,
    token,
    material,
    onSubmitSuccess
  );

  const isFullyConfirmedOnLoad = useMemo(
    () => hasAllMilestoneActualDatesFromApi(material),
    [material]
  );

  const isOffsetMode = material.request_type === "OFFSET";
  const isSubmittedOnce = material.estimate_status === EstimateStatus.SUBMITTED;
  const timelineVariant = getMaterialTimelineVariant(
    material.dateblock_compute_strategy
  );
  const disableFirstMilestoneSetActual =
    !timelineVariant.forceFirstMilestoneReadOnly &&
    material.ready_for_release === false &&
    material.workflow_status !== "IN_PROGRESS";

  const [offsetForm] = Form.useForm();
  const [offsetSubmitting, setOffsetSubmitting] = useState(false);
  const [offsetHasErrors, setOffsetHasErrors] = useState(false);

  const onConfirmOffsets = useCallback(async () => {
    let values: Record<string, number | string>;
    try {
      values = await offsetForm.validateFields();
    } catch {
      return;
    }

    if (!token) {
      message.error("Failed to update lead time");
      return;
    }

    setOffsetSubmitting(true);
    const payload = mapOffsetsToApiPayload(material, values, data.comments);
    const response = await updateLeadTimeRequestDetails(token, payload);
    if (response.success) {
      onSubmitSuccess?.();
    } else {
      message.error("Failed to update lead time");
    }
    setOffsetSubmitting(false);
  }, [offsetForm, token, material, data.comments, onSubmitSuccess]);

  return isOffsetMode ? (
    <div
      className="material-date-confirmation flex flex-col lg:flex-row h-full bg-white overflow-hidden"
      data-testid="material-date-confirmation-main-content"
    >
      <div className="material-date-confirmation-main flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden lg:border-0 lg:border-r lg:border-solid lg:border-r-ciq-gray-400">
        <MaterialDateConfirmationHeader
          data={data}
          detailsData={data}
          expectedOnsiteDateLabel={timelineVariant.expectedOnsiteDateLabel}
        />
        <MaterialContentCommentsLayout
          testId="material-offset-section"
          content={
            <MaterialDurationsSection
              material={material}
              form={offsetForm}
              onValidityChange={setOffsetHasErrors}
            />
          }
          comments={
            <MaterialComments
              value={data.comments}
              onChange={onCommentsChange}
            />
          }
        />
        <MaterialDateConfirmationFooter
          submitting={offsetSubmitting}
          disabled={offsetHasErrors}
          onConfirmDates={onConfirmOffsets}
          buttonText={isSubmittedOnce ? "Submit Changes" : "Confirm Lead Time"}
        />
      </div>
      <div className="hidden lg:block shrink-0 lg:h-full">
        <MaterialDetailsSidebar data={data} />
      </div>
    </div>
  ) : (
    <MaterialDateConfirmationMainContent
      data={data}
      timezoneId={timezoneId}
      calendarOffs={calendarOffs}
      submitting={submitting}
      hasDateChanges={hasDateChanges}
      hasProjectedDateOrderError={hasProjectedDateOrderError}
      projectedDateErrorIds={projectedDateErrorIds}
      hasActualDateOrderError={hasActualDateOrderError}
      actualDateErrorIds={actualDateErrorIds}
      isFullyConfirmedOnLoad={isFullyConfirmedOnLoad}
      projectedDateLabel={timelineVariant.projectedDateLabel}
      expectedOnsiteDateLabel={timelineVariant.expectedOnsiteDateLabel}
      forceFirstMilestoneReadOnly={timelineVariant.forceFirstMilestoneReadOnly}
      canMarkCompleteWhenNoPrevious={
        timelineVariant.canMarkCompleteWhenNoPrevious
      }
      disableFirstMilestoneSetActual={disableFirstMilestoneSetActual}
      onMilestoneChange={onMilestoneChange}
      onCommentsChange={onCommentsChange}
      onMarkMilestoneComplete={onMarkMilestoneComplete}
      onConfirmDates={onConfirmDates}
    />
  );
}
